import React, { useState } from 'react';
import { ACCENT, STANDING_COLORS, capsOf, capabilitiesFor } from './supplierUi';

// Interactive supplier ↔ capability "brain map" (SVG, no chart library).
// Default: a bipartite network — suppliers on the left, capabilities on the
// right, an edge for every capability a supplier offers. Focusing a capability
// (dropdown or click) re-lays the map radially: the capability in the middle,
// every supplier that can do it around it, with their rating. Focusing a
// supplier shows the inverse: everything that supplier does for us.
// `categories` is the merged canonical+custom vocabulary computed by the parent
// (SuppliersLibrary) so the filter dropdown and this map can never drift.

const pillW = (label) => Math.max(70, String(label).length * 6.8 + 28);

function NodePill({ x, y, label, sub, color, active, dim, onClick, onHover }) {
  const w = pillW(label);
  return (
    <g onClick={onClick} onMouseEnter={() => onHover && onHover(true)} onMouseLeave={() => onHover && onHover(false)}
      style={{ cursor: 'pointer', opacity: dim ? 0.22 : 1 }}>
      <rect x={x - w / 2} y={y - 15} width={w} height={30} rx={15}
        style={{ fill: 'var(--ni-panel)', stroke: color, strokeWidth: active ? 2.5 : 1.2 }} />
      <text x={x} y={y + 4} textAnchor="middle" style={{ fill: 'var(--ni-text)', fontSize: 12, fontWeight: 700 }}>{label}</text>
      {sub ? <text x={x} y={y + 30} textAnchor="middle" style={{ fill: ACCENT, fontSize: 11, letterSpacing: 1 }}>{sub}</text> : null}
    </g>
  );
}

function SupplierCapabilityMap({ items, categories, onOpenSupplier, filtersActive }) {
  const [focus, setFocusState] = useState(null);   // { kind: 'cap'|'sup', value } | null
  const [hover, setHover] = useState(null);

  // Changing focus swaps the layout and unmounts the hovered pill — React never
  // fires mouseleave for removed nodes, so always drop hover alongside.
  const setFocus = (next) => { setFocusState(next); setHover(null); };

  const usedSet = new Set(items.flatMap(capsOf));
  const mapCats = categories.filter(c => usedSet.has(c));   // drawn: in use

  const focusCap = focus && focus.kind === 'cap' ? focus.value : null;
  const focusSupplier = focus && focus.kind === 'sup' ? items.find(s => s._id === focus.value) : null;

  const hi = focus || hover;
  const supHi = (s) => !!hi && ((hi.kind === 'sup' && hi.value === s._id) || (hi.kind === 'cap' && capsOf(s).includes(hi.value)));
  const catHi = (cat) => {
    if (!hi) return false;
    if (hi.kind === 'cap') return hi.value === cat;
    const sup = items.find(s => s._id === hi.value);
    return !!sup && capsOf(sup).includes(cat);
  };

  const toggleCap = (cat) => setFocus(focus && focus.kind === 'cap' && focus.value === cat ? null : { kind: 'cap', value: cat });
  const toggleSup = (id) => setFocus(focus && focus.kind === 'sup' && focus.value === id ? null : { kind: 'sup', value: id });

  const sel = { background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', color: 'var(--ni-text)', borderRadius: 6, padding: '6px 10px', fontSize: 13 };
  const clearBtn = { background: 'transparent', color: 'var(--ni-text-muted)', border: '1px solid var(--ni-border-soft)', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' };

  // ── Radial layout (a focused capability or supplier in the centre) ──────────
  const renderRadial = (center, ring, emptyText) => {
    const n = ring.length;
    const r = Math.max(170, 70 + n * 17);
    const W = Math.max(960, 2 * r + 280);   // widen with the ring so 3/9-o'clock pills never clip
    const H = 2 * r + 170;
    const cx = W / 2, cy = H / 2;
    return (
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', minWidth: 720, display: 'block' }}>
        {ring.map((node, i) => {
          const a = ((-90 + (i * 360) / Math.max(n, 1)) * Math.PI) / 180;
          const x = cx + r * Math.cos(a);
          const y = cy + r * Math.sin(a);
          return (
            <g key={node.key}>
              <line x1={cx} y1={cy} x2={x} y2={y} style={{ stroke: ACCENT, strokeOpacity: 0.45, strokeWidth: 1.5 }} />
              <NodePill x={x} y={y} label={node.label} sub={node.sub} color={node.color} onClick={node.onClick} />
            </g>
          );
        })}
        <g onClick={() => setFocus(null)} style={{ cursor: 'pointer' }}>
          <rect x={cx - pillW(center.label) / 2 - 10} y={cy - 22} width={pillW(center.label) + 20} height={44} rx={22}
            style={{ fill: 'var(--ni-panel)', stroke: center.color, strokeWidth: 3 }} />
          <text x={cx} y={cy - 1} textAnchor="middle" style={{ fill: 'var(--ni-text)', fontSize: 14, fontWeight: 800 }}>{center.label}</text>
          <text x={cx} y={cy + 15} textAnchor="middle" style={{ fill: 'var(--ni-text-muted)', fontSize: 10 }}>{center.sub}</text>
        </g>
        {n === 0 && (
          <text x={cx} y={cy + 70} textAnchor="middle" style={{ fill: 'var(--ni-text-muted)', fontSize: 13 }}>{emptyText}</text>
        )}
      </svg>
    );
  };

  let body;
  if (focusCap) {
    const ring = items.filter(s => capsOf(s).includes(focusCap)).map(s => ({
      key: s._id,
      label: s.name,
      sub: `${capabilitiesFor(s, focusCap).length} recorded process(es)`,
      color: STANDING_COLORS[s.standing] || '#666',
      onClick: () => toggleSup(s._id),
    }));
    body = renderRadial(
      { label: focusCap, sub: `${ring.length} supplier${ring.length === 1 ? '' : 's'} · click to reset`, color: ACCENT },
      ring,
      filtersActive
        ? `No suppliers match “${focusCap}” under the current filters — clear them to check the whole matrix.`
        : `No suppliers have recorded “${focusCap}” yet — scope and evidence need review.`,
    );
  } else if (focusSupplier) {
    const ring = capsOf(focusSupplier).map(cat => ({
      key: cat,
      label: cat,
      sub: `${capabilitiesFor(focusSupplier, cat).length} recorded process(es)`,
      color: ACCENT,
      onClick: () => toggleCap(cat),
    }));
    body = renderRadial(
      { label: focusSupplier.name, sub: `${ring.length} capabilit${ring.length === 1 ? 'y' : 'ies'} · click to reset`, color: STANDING_COLORS[focusSupplier.standing] || '#666' },
      ring, 'No capabilities recorded for this supplier yet.',
    );
  } else {
    // ── Bipartite layout: suppliers ↔ capabilities ──────────────────────────
    const W = 960, rowH = 44, supX = 210, catX = 750;
    const rows = Math.max(items.length, mapCats.length, 1);
    const H = rows * rowH + 80;
    const colY = (count, i) => 60 + (rows - count) * rowH / 2 + i * rowH + rowH / 2;
    const edges = [];
    items.forEach((s, i) => {
      capsOf(s).forEach(cat => {
        const j = mapCats.indexOf(cat);
        if (j === -1) return;
        edges.push({ s, cat, x1: supX + pillW(s.name) / 2, y1: colY(items.length, i), x2: catX - pillW(cat) / 2, y2: colY(mapCats.length, j) });
      });
    });
    body = (
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', minWidth: 720, display: 'block' }}>
        <text x={supX} y={32} textAnchor="middle" style={{ fill: 'var(--ni-text-muted)', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Suppliers</text>
        <text x={catX} y={32} textAnchor="middle" style={{ fill: 'var(--ni-text-muted)', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Capabilities</text>
        {edges.map((e, k) => {
          const lit = supHi(e.s) && catHi(e.cat);
          const mx = (e.x1 + e.x2) / 2;
          return (
            <path key={k} d={`M ${e.x1} ${e.y1} C ${mx} ${e.y1}, ${mx} ${e.y2}, ${e.x2} ${e.y2}`}
              style={{ fill: 'none', stroke: lit ? ACCENT : 'var(--ni-border-soft)', strokeWidth: lit ? 2.2 : 1.2, strokeOpacity: hi && !lit ? 0.15 : lit ? 0.95 : 0.7 }} />
          );
        })}
        {items.map((s, i) => (
          <NodePill key={s._id} x={supX} y={colY(items.length, i)} label={s.name}
            color={STANDING_COLORS[s.standing] || '#666'} active={supHi(s)} dim={!!hi && !supHi(s)}
            onClick={() => toggleSup(s._id)}
            onHover={(on) => setHover(on ? { kind: 'sup', value: s._id } : null)} />
        ))}
        {mapCats.map((cat, j) => (
          <NodePill key={cat} x={catX} y={colY(mapCats.length, j)} label={cat}
            color={ACCENT} active={catHi(cat)} dim={!!hi && !catHi(cat)}
            onClick={() => toggleCap(cat)}
            onHover={(on) => setHover(on ? { kind: 'cap', value: cat } : null)} />
        ))}
      </svg>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '12px 16px', borderBottom: '1px solid var(--ni-border-soft)' }}>
        <select value={focusCap || ''} onChange={e => setFocus(e.target.value ? { kind: 'cap', value: e.target.value } : null)} style={sel}>
          <option value="">Focus a capability…</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {focusSupplier && (
          <button onClick={() => onOpenSupplier(focusSupplier._id)}
            style={{ background: ACCENT, color: '#000', border: 'none', borderRadius: 6, padding: '6px 14px', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
            Open {focusSupplier.name} →
          </button>
        )}
        {focus && <button onClick={() => setFocus(null)} style={clearBtn}>✕ Clear focus</button>}
        <span style={{ fontSize: 12, color: 'var(--ni-text-muted)' }}>
          {filtersActive
            ? `Filters are active — the map shows ${items.length} matching supplier${items.length === 1 ? '' : 's'}, not the whole matrix.`
            : 'Click a supplier or capability to focus it — the map re-centres around what you picked.'}
        </span>
      </div>
      <div style={{ overflowX: 'auto', padding: '8px 0' }}>{body}</div>
    </div>
  );
}

export default React.memo(SupplierCapabilityMap);
